import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { IconPause, IconPlay, IconMobileAlt, IconSeedling, IconChartLine, IconFolderOpen } from '../ui/icons';
import { usePlatformStats, formatReturnRange } from '../../services/platformStats';
import { MIN_INVESTMENT_LABEL, PAYMENT_METHODS_TEXT } from '../../config/paymentMethods';

import './HeroCarousel.css';

import ImpactInvestImg from '../../assets/icones/ImpactInvest.jpg';
import communityPhoto from '../../assets/icones/communityPhoto.jpg';
import impactInvestSpace from '../../assets/icones/impact invest.jpg';

/**
 * 🏠 BANDEAU D'ACCUEIL
 * ===================
 * - Message unique et stable (un seul H1) : ce que fait la plateforme, pour qui, comment
 * - Deux parcours : investir (investisseurs) / financer son projet (entrepreneurs)
 * - Chiffres réels (src/services/platformStats.js : projets publiés + paiements confirmés)
 * - Images de fond décoratives en fondu, avec bouton pause ; immobiles si l'utilisateur
 *   a demandé à réduire les animations
 */

const BACKGROUNDS = [ImpactInvestImg, communityPhoto, impactInvestSpace];

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const HeroCarousel = () => {
  const stats = usePlatformStats();
  const [current, setCurrent] = useState(0);
  const [playing, setPlaying] = useState(() => !prefersReducedMotion());

  useEffect(() => {
    if (!playing) return undefined;
    const timer = setInterval(() => setCurrent(index => (index + 1) % BACKGROUNDS.length), 7000);
    return () => clearInterval(timer);
  }, [playing]);

  const facts = [
    { icon: IconFolderOpen, value: `${stats.openProjects}`, label: stats.openProjects > 1 ? 'projets ouverts' : 'projet ouvert' },
    { icon: IconSeedling, value: MIN_INVESTMENT_LABEL, label: 'pour commencer' },
    stats.minReturn !== null && { icon: IconChartLine, value: formatReturnRange(stats), label: 'rendement annuel visé' },
    { icon: IconMobileAlt, value: 'Mobile Money', label: 'Airtel, Orange, BIA CASH' }
  ].filter(Boolean);

  return (
    <section className="hero-section" aria-labelledby="hero-title">
      <div className="hero-backgrounds" aria-hidden="true">
        {BACKGROUNDS.map((src, index) => (
          <img
            key={src}
            src={src}
            alt=""
            className={`hero-background ${index === current ? 'active' : ''}`}
            loading={index === 0 ? 'eager' : 'lazy'}
            fetchPriority={index === 0 ? 'high' : undefined}
            decoding="async"
          />
        ))}
        <div className="hero-overlay" />
      </div>

      <div className="hero-inner">
        <p className="hero-eyebrow">Plateforme d'investissement à impact · Niger</p>

        <h1 id="hero-title" className="hero-title">
          Investissez dans les projets qui font avancer le Sahel
        </h1>

        <p className="hero-lead">
          Financez des entrepreneurs locaux à partir de {MIN_INVESTMENT_LABEL}, ou devenez associé en achetant
          des parts dans leurs projets. Payez simplement par {PAYMENT_METHODS_TEXT}, puis suivez en temps réel l'impact de votre argent depuis votre portefeuille.
        </p>

        <div className="hero-actions">
          <Link to="/projects" className="hero-cta-primary">
            Découvrir les projets
          </Link>
          <Link to="/financement" className="hero-cta-secondary">
            Faire financer mon projet
          </Link>
        </div>

        <ul className="hero-facts">
          {facts.map(({ icon: Icon, value, label }) => (
            <li key={label} className="hero-fact">
              <Icon className="hero-fact-icon" aria-hidden="true" />
              <span>
                <strong>{value}</strong>
                <span className="hero-fact-label">{label}</span>
              </span>
            </li>
          ))}
        </ul>

        <p className="hero-risk">
          Un bon investissement commence par une bonne analyse. Investir comporte un risque de perte en capital
          et d'immobilisation de votre argent ; les rendements visés ne sont pas garantis.
        </p>
      </div>

      <button
        type="button"
        className="hero-pause"
        onClick={() => setPlaying(value => !value)}
        aria-label={playing ? 'Mettre en pause le défilement des images' : 'Reprendre le défilement des images'}
        title={playing ? 'Pause' : 'Lecture'}
      >
        {playing ? <IconPause aria-hidden="true" /> : <IconPlay aria-hidden="true" />}
      </button>
    </section>
  );
};

export default HeroCarousel;
